// @flow

import {
  Order,
  OrderProduct,
  OrderMenu,
  OrderMenuPart,
  OrderProductOption,
  OrderProductOptionValue,
} from 'PMT/entities/order/Order';
import { PhoneUtils, OrderViewUtils } from 'PMT/utils';
import Restaurant from 'PMT/entities/Restaurant';
import _ from 'lodash';
import I18n from 'PMT/i18n/I18n';

import ArrayUtils from 'PMT/utils/ArrayUtils';
import CurrencyUtils from 'PMT/utils/CurrencyUtils';

const PAPER_WIDTH = {
  PAPER_WIDTH_58MM: 0, // 58mm Reciept
  PAPER_WIDTH_80MM: 1, // 80mm Reciept
};

const WIDTH_58mm_FONT_B = 35;
const WIDTH_58mm_SUBTITLE = 25;
const WIDTH_80mm_FONT_B = 43;
const WIDTH_SMALL_TEXT = 53;

/**
 * Format the amount to display
 * @param	str	String	String to put a comma to separate thousands
 */
function formatPrice(amount: number) {
  return CurrencyUtils.formatPrice(amount);
}

/**
 * TODO: translations I18n
 * TODO: currency
 */
class EspsonPrinter {
  order: Order = null;
  restaurant: Restaurant = null;

  builder = new epson.ePOSBuilder();

  paperWidth = PAPER_WIDTH.PAPER_WIDTH_80MM;

  DEFAULT_LEFT_BORDER: number = 5;
  MARGIN_LEFT: number = 35;
  MARGIN_LEFT_SECOND_COL: number = 65;

  formatOrderTicket(order: Order, restaurant: Restaurant): string {
    this.order = order;
    this.restaurant = restaurant;

    // Open drawer
    // TODO: ?
    // this.builder.addPulse(this.builder.DRAWER_1, this.builder.PULSE_100);

    // empty first line
    this.builder.addText(`\n`);

    this.renderHeader();

    this.renderMenus();

    this.renderProducts();

    this.renderFooter();

    this.builder.addFeedLine(3);
    this.builder.addCut(this.builder.CUT_FEED);

    const message = this.builder.toString();

    console.log(message);

    return message;
  }

  renderHeader() {
    this.builder.addTextAlign(this.builder.ALIGN_CENTER);

    this.setSubtitle('PayMyTable\n', { bold: true });

    this.builder.addFeedLine(1);

    this.renderRestaurant();

    this.builder.addFeedLine(1);

    this.setTitle(`Commande ${this.order.getOrderNumber()}\n`, { bold: true });

    this.builder.addFeedLine(1);

    if (this.order.isPayed()) {
      if (this.order.userAccountPayment !== null) {
        this.setTitle('Payée (Compte)\n');
      } else {
        this.setTitle('Payée (CB)\n');
      }
    } else {
      this.setTitle('Non payée\n');
    }

    this.builder.addFeedLine(1);

    if (this.order.isDeliveryMode()) {
      this.setSubtitle(`${OrderViewUtils.getLabelForMode(this.order.mode)}\n`, {
        bold: true,
      });
      this.builder.addFeedLine(1);
      this.renderOrderDueDate();
    } else if (this.order.isOnSiteMode()) {
      if (this.order.isOnBar()) {
        this.setSubtitle('Au bar\n'); // TODO: I18n
      } else {
        this.setSubtitle(
          `${OrderViewUtils.getLabelForMode(this.order.mode)}\n`,
          { bold: true }
        );
      }
      this.builder.addFeedLine(1);
      this.renderOrderDueDate();
    } else if (this.order.isTakeAwayMode()) {
      this.setSubtitle(`${OrderViewUtils.getLabelForMode(this.order.mode)}\n`, {
        bold: true,
      });
      this.builder.addFeedLine(1);
      this.renderOrderDueDate();
    }

    this.renderClientInfo();

    // -- global comment
    const globalComment = this.order.comment;
    if (!_.isEmpty(globalComment)) {
      this.builder.addText('\n');
      this.builder.addTextAlign(this.builder.ALIGN_LEFT);
      this.builder.addTextPosition(this.DEFAULT_LEFT_BORDER);
      this.setSubtitle(`*** Commentaire général :\n`, { bold: true });
      this.setText(`${globalComment.toUpperCase()}\n`, { bold: true });
    }

    // this.builder.addFeedLine(1);
    //
    // this.builder.addTextAlign(this.builder.ALIGN_CENTER);
    // this.setSmallText(`${this.order.countItems()} produit(s)`);

    this.builder.addFeedLine(2);
  }

  renderOrderDueDate() {
    this.setSubtitle(
      `Pour le ${this.order.dueDate.format(
        'DD/MM'
      )} à ${this.order.dueDate.format('HH:mm')}\n`
    );
  }

  renderRestaurant() {
    this.setText(`${this.restaurant.name.toUpperCase()}\n`);

    const address = this.restaurant.address;

    if (!_.isNil(address)) {
      const addressTxt = `${address.street}\n${address.postCode} ${address.city}\n`;
      this.setSmallText(addressTxt);
    }

    if (!_.isEmpty(this.restaurant.legal.siren)) {
      this.setSmallText(`SIRET: ${this.restaurant.legal.siren}\n`);
    }
  }

  renderClientInfo() {
    const user = this.order.user;

    this.builder.addFeedLine(1);

    if (user) {
      const clientName = `${user.firstName} ${user.lastName}`;
      this.setText(`Client : ${clientName.toUpperCase()}\n`);
      this.setText(`Tel : ${PhoneUtils.format(user.phone)}\n`);
    }

    if (this.order.isDeliveryMode()) {
      // address
      const address = this.order.address;
      if (!_.isNil(address)) {
        this.builder.addFeedLine(1);
        this.setText(`${address.street}\n`);
        this.setText(`${address.postCode} ${address.city}\n`);

        if (!_.isEmpty(address.complement)) {
          this.setText(`${address.complement}\n`);
        }
        if (!_.isEmpty(address.instructions)) {
          this.setText(`${address.instructions}\n`);
        }
      }
    } else if (!_.isEmpty(this.order.tableNumber)) {
      this.builder.addFeedLine(1);
      this.setSubtitle(`Table ${this.order.tableNumber}\n`, { bold: true });
    }
  }

  renderMenus() {
    this.builder.addTextAlign(this.builder.ALIGN_LEFT);

    this.order.getMenusWithQuantities().forEach((menuWithQuantity: Object) => {
      const quantity = menuWithQuantity.quantity;
      const orderMenu: OrderMenu = menuWithQuantity.orderMenu;

      this.builder.addTextPosition(this.DEFAULT_LEFT_BORDER);

      this.renderItemTitle(
        orderMenu.name,
        quantity,
        quantity * orderMenu.getTotalPrice()
      );

      orderMenu.parts.forEach((orderMenuPart: OrderMenuPart) => {
        orderMenuPart.products.forEach((orderProduct: OrderProduct) => {
          this.builder.addTextPosition(this.MARGIN_LEFT);
          this.setText(`${orderProduct.name}\n`);

          orderProduct.options.forEach(
            (orderProductOption: OrderProductOption) => {
              orderProductOption.values.forEach(
                (orderProductOptionValue: OrderProductOptionValue) => {
                  this.builder.addTextPosition(this.MARGIN_LEFT_SECOND_COL);
                  this.setText(
                    `${orderProductOption.name}: ${orderProductOptionValue.name}\n`
                  );
                }
              );
            }
          );

          // comment
          let comment = '';
          if (!_.isEmpty(orderProduct.comment)) {
            comment = `${orderProduct.comment.toUpperCase()}`;
          }
          if (!_.isEmpty(comment)) {
            this.builder.addTextPosition(this.MARGIN_LEFT_SECOND_COL);
            this.setText('*** Commentaire\n', { bold: true });
            this.builder.addTextPosition(this.MARGIN_LEFT_SECOND_COL);
            this.setText(`${comment.toUpperCase()}\n`, { bold: true });
          }
        });
      });

      this.builder.addFeedLine(1);
    });
  }

  renderProducts() {
    this.builder.addTextAlign(this.builder.ALIGN_LEFT);

    this.order.getProductsWithQuantities().forEach(productWithQuantity => {
      const quantity = productWithQuantity.quantity;
      const orderProduct: OrderProduct = productWithQuantity.orderProduct;

      this.builder.addTextPosition(this.DEFAULT_LEFT_BORDER);
      this.renderItemTitle(
        orderProduct.name,
        quantity,
        quantity * orderProduct.getTotalPrice()
      );

      orderProduct.options.forEach((orderProductOption: OrderProductOption) => {
        orderProductOption.values.forEach(
          (orderProductOptionValue: OrderProductOptionValue) => {
            this.builder.addTextPosition(this.MARGIN_LEFT_SECOND_COL);
            this.setText(
              `${orderProductOption.name}: ${orderProductOptionValue.name}\n`
            );
          }
        );
      });

      if (!_.isEmpty(orderProduct.comment)) {
        this.builder.addTextPosition(this.MARGIN_LEFT);
        this.setText('*** Commentaire\n', { bold: true });
        this.builder.addTextPosition(this.MARGIN_LEFT);
        this.setText(`${orderProduct.comment.toUpperCase()}\n`, { bold: true });
      }

      this.builder.addFeedLine(1);
    });
  }

  renderItemTitle(name: string, quantity: number, amount: Price) {
    let strName = '';
    let strQuantity = '';
    let strSubtotal = '';
    let spaces = '';
    let prefix = '';

    switch (this.paperWidth) {
      case PAPER_WIDTH.PAPER_WIDTH_58MM:
        // Product name
        strName = name;

        // Quantity
        strQuantity = quantity + 'x  ';

        // Subtotal
        strSubtotal = formatPrice(amount);

        spaces = this.fillWithSpaces(
          prefix.length +
            strQuantity.length +
            strName.length +
            strSubtotal.length,
          WIDTH_58mm_FONT_B
        );
        break;

      case PAPER_WIDTH.PAPER_WIDTH_80MM:
        // Product name
        strName = name;

        // Quantity
        strQuantity = quantity + 'x  ';

        // Subtotal
        strSubtotal = formatPrice(amount);

        spaces = this.fillWithSpaces(
          prefix.length +
            strQuantity.length +
            strName.length +
            strSubtotal.length,
          WIDTH_80mm_FONT_B
        );
        break;

      default:
        break;
    }

    this.setText(
      `${prefix}${strQuantity}${strName}${spaces}${strSubtotal}€\n`,
      { bold: true }
    );
  }

  renderFooter() {
    this.builder.addText('\n\n');

    // Set total, net and tax for reciept printing
    let strTotal = formatPrice(this.order.getTotalPrice());

    let taxesLines = [];

    let accountPaymentLines = [];

    // net
    // strTotal - strTax

    const userAccountPayment = this.order.userAccountPayment;

    const totalLabel = 'TOTAL';

    // TODO: i18n
    const accountPaymentLabel = 'paiement compte :';

    switch (this.paperWidth) {
      case PAPER_WIDTH.PAPER_WIDTH_58MM:
        // Total
        strTotal = `${this.fillWithSpaces(
          totalLabel.length + strTotal.length + 1,
          WIDTH_58mm_SUBTITLE
        )}${strTotal}€`;

        if (!_.isNil(userAccountPayment)) {
          let accountPaymentTotal = formatPrice(
            userAccountPayment.amountFromAccount
          );

          accountPaymentLines[0] =
            accountPaymentLabel +
            this.fillWithSpaces(
              accountPaymentLabel.length + accountPaymentTotal.length + 1,
              WIDTH_58mm_SUBTITLE
            ) +
            accountPaymentTotal +
            `€`;

          if (userAccountPayment.grantAmount !== 0) {
            let grantAmount = formatPrice(userAccountPayment.grantAmount);

            const grantLabel: string = _.isNil(userAccountPayment.grantLabel)
              ? ''
              : userAccountPayment.grantLabel;
            accountPaymentLines[1] =
              grantLabel +
              this.fillWithSpaces(
                grantLabel.length + grantAmount.length + 1,
                WIDTH_58mm_SUBTITLE
              ) +
              grantAmount +
              `€`;
          }
        }

        // Tax
        taxesLines = _.map(this.order.check.tax, taxDetail => {
          const tax = parseFloat(taxDetail.amoutOfTax);
          if (!tax || tax === 0) {
            return null;
          }
          const strTax = formatPrice(tax);
          const tvaPrefix =
            taxDetail.name.toLowerCase().indexOf('tva') === -1 ? 'TVA ' : '';
          const taxLabel = `${tvaPrefix}${taxDetail.name}`;
          return `${taxLabel}${this.fillWithSpaces(
            taxLabel.length + strTax.length + 1,
            WIDTH_58mm_SUBTITLE
          )}${strTax}€`;
        }).filter(taxLine => taxLine !== null);
        break;

      case PAPER_WIDTH.PAPER_WIDTH_80MM:
        // Total
        strTotal = `${this.fillWithSpaces(
          totalLabel.length + strTotal.length + 1,
          27
        )}${strTotal}€`;

        if (!_.isNil(userAccountPayment)) {
          let accountPaymentTotal = formatPrice(
            userAccountPayment.amountFromAccount
          );

          accountPaymentLines[0] =
            accountPaymentLabel +
            this.fillWithSpaces(
              accountPaymentLabel.length + accountPaymentTotal.length + 1,
              WIDTH_SMALL_TEXT
            ) +
            accountPaymentTotal +
            `€`;

          if (userAccountPayment.grantAmount !== 0) {
            let grantAmount = formatPrice(userAccountPayment.grantAmount);
            const grantLabel: string = _.isNil(userAccountPayment.grantLabel)
              ? ''
              : userAccountPayment.grantLabel;

            accountPaymentLines[1] =
              grantLabel +
              this.fillWithSpaces(
                grantLabel.length + grantAmount.length + 1,
                WIDTH_SMALL_TEXT
              ) +
              grantAmount +
              `€`;
          }
        }

        // Tax
        taxesLines = _.map(this.order.check.tax, taxDetail => {
          const tax = parseFloat(taxDetail.amoutOfTax);
          if (tax === 0 || !tax) {
            return null;
          }
          const strTax = formatPrice(tax);
          const tvaPrefix =
            taxDetail.name.toLowerCase().indexOf('tva') === -1 ? 'TVA ' : '';
          const taxLabel = `${tvaPrefix}${taxDetail.name}`;
          return `${taxLabel}${this.fillWithSpaces(
            taxLabel.length + strTax.length + 1,
            WIDTH_SMALL_TEXT
          )}${strTax}€`;
        }).filter(taxLine => taxLine !== null);
        break;

      default:
    }

    // Fees
    _.each(this.order.fees, fee => {
      const name = ArrayUtils.find(
        fee.name,
        locale =>
          locale.locale &&
          _.startsWith(
            locale.locale.toLowerCase(),
            I18n.getCurrentLanguageWithoutRegionCode()
          )
      );
      this.setSmallText(
        `${(name || fee.name[0]).text} : ${formatPrice(fee.amountIncludingTax)}€\n`
      );
    });

    // Total
    this.setSubtitle(`${totalLabel}${strTotal}\n`);

    this.builder.addFeedLine(1);

    if (!_.isEmpty(accountPaymentLines)) {
      _.each(accountPaymentLines, (str: string) => {
        this.setSmallText(`${str}\n`);
        this.builder.addTextSize(1);
      });
    }

    if (this.order.isPayed()) {
      _.each(taxesLines, (strTax: string) => {
        if (strTax) {
          this.setSmallText(`${strTax}\n`);
          this.builder.addTextSize(1);
        }
      });
    }

    this.builder.addFeedLine(1);
    const createdDate = `${this.order.creationDate.format(
      'DD/MM/YYYY'
    )} - ${this.order.creationDate.format('HH:mm')}`;

    this.setSmallText(`Commande passée le : ${createdDate}\n`);

    this.builder.addFeedLine(2);
  }

  //
  // --------------------------------------------------------------------------------------------
  //        UTILS
  // --------------------------------------------------------------------------------------------
  //

  setTitle(title: string, options = {}) {
    this.builder.addTextFont(this.builder.FONT_A);
    this.builder.addTextSize(2, 2);
    this.handleOptionsBegin(options);
    this.builder.addText(title);
    this.handleOptionsEnd(options);
  }

  setSubtitle(subtitle: string, options = {}) {
    this.builder.addTextFont(this.builder.FONT_B);
    this.builder.addTextSize(2, 2);
    this.handleOptionsBegin(options);
    this.builder.addText(subtitle);
    this.handleOptionsEnd(options);
  }

  setText(text: string, options = {}) {
    this.builder.addTextFont(this.builder.FONT_A);
    this.builder.addTextSize(1, 1);
    this.handleOptionsBegin(options);
    this.builder.addText(text);
    this.handleOptionsEnd(options);
  }

  setSmallText(text: string, options = {}) {
    this.builder.addTextFont(this.builder.FONT_B);
    this.builder.addTextSize(1, 1);
    this.handleOptionsBegin(options);
    this.builder.addText(text);
    this.handleOptionsEnd(options);
  }

  handleOptionsBegin(options) {
    if (options.bold === true) {
      this.beginBold();
    }
  }

  handleOptionsEnd(options) {
    if (options.bold === true) {
      this.endBold();
    }
  }

  beginBold() {
    this.builder.addTextStyle(false, false, true, this.builder.COLOR_1);
  }

  endBold() {
    this.builder.addTextStyle(false, false, false, this.builder.COLOR_1);
  }

  /*
   * Note: only work for FONT_A
   */
  fillWithSpaces(nb: number, width: number): string {
    return this.generateSpaces(width - nb);
  }

  generateSpaces(nb: number): string {
    let str = '';

    for (let i = 0; i < nb; i++) {
      str += ' ';
    }

    return str;
  }
}

export default EspsonPrinter;
